import React, { useState, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Form, Input, Button, Col } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import OtpInput from 'react-otp-input';

import { AuthenticationCredentialsRequest } from '@openapi';
import auth from '@services/api/auth';

import 'react-toastify/dist/ReactToastify.css';
import './login.scss';
import logo from '@assets/images/sage-logo-login.png';
import { toast } from 'react-toastify';
import styles from './login-auth.module.scss';
import { AppContext } from '@layouts/AppRouter';
import { Navigation } from '@constants/Navigation';
import { ApiError } from '@services/types';
import { getBackendOrigin } from '@services/api/configuration';

function Login() {
  const [form] = Form.useForm<AuthenticationCredentialsRequest>();
  const tokenContext = useContext(AppContext);
  const history = useHistory();
  const isSaml = tokenContext.variablesProfile?.profile === 'saml';

  const [mfaStep, setMfaStep] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [otp, setOtp] = useState('');
  const [qrCodeUri, setQrCodeUri] = useState<string | null>(null);

  const handleFinish = async (values: AuthenticationCredentialsRequest) => {
    await form.validateFields();
    try {
      const result = await auth.login(values);

      if (result.accessToken) {
        tokenContext.setToken(result.accessToken);
        tokenContext.setUserLoggedIn(true);
        localStorage.setItem('userLoggedIn', JSON.stringify(true));
      } else if (result.mfaRequired && !isSaml) {
        if (result.qrCodeUri) {
          setQrCodeUri(result.qrCodeUri);
          setShowQr(true);
          toast.info('Scan the QR code with Google Authenticator and enter the code');
        } else {
          setMfaStep(true);
          toast.info('Enter your MFA code from Google Authenticator');
        }
      }
    } catch (error: any) {
      if (error?.error?.userId) {
        tokenContext.setUserId(error?.error?.userId);
        toast.error('Password is outdated and needs to be updated');
        setTimeout(() => history.push(Navigation.ChangePassword), 3000);
      } else if (error instanceof ApiError) {
        toast.error(error?.error.details);
      } else {
        toast.error('Auth error', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  };

  const handleVerifyMfa = async () => {
    if (!otp) {
      toast.error('Please enter the MFA code');
      return;
    }
    try {
      const result = await auth.verifyMfa({
        verifyMfaRequest: {
          userEmail: form.getFieldValue('username'),
          mfaCode: parseInt(otp, 10),
        },
      });

      if (result.accessToken) {
        tokenContext.setToken(result.accessToken);
        tokenContext.setUserLoggedIn(true);
        localStorage.setItem('userLoggedIn', JSON.stringify(true));

        setMfaStep(false);
        setShowQr(false);
        setOtp('');
        setQrCodeUri(null);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.error?.details ?? 'Invalid MFA code');
    } finally {
      setOtp('');
    }
  };

  return tokenContext.url === 'default' ? (
    <div className="main-login-page">
      <div className="login-wrapper">
        <img className="login-logo" src={logo} alt="Logo Sagem" />

        {!mfaStep && !showQr && (
          <Form className={styles.form} name="normal_login" onFinish={handleFinish} form={form}>
            <Form.Item
              className={styles.block}
              name="username"
              rules={[{ required: true, message: 'Please input your Username!' }]}
            >
              <Input
                className={styles.input}
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder="Username"
              />
            </Form.Item>
            <Form.Item
              className={styles.block}
              name="password"
              rules={[{ required: true, message: 'Please input your Password!' }]}
            >
              <Input.Password
                className={styles.input}
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Password"
              />
            </Form.Item>
            <Form.Item className="auth-footer">
              <Button className={styles.button} type="primary" htmlType="submit">
                Log in
              </Button>
            </Form.Item>
            <Form.Item>
              <Link className="login-form-forgot" to={Navigation.RecoverPassword}>
                Forgot password
              </Link>
            </Form.Item>
          </Form>
        )}

        {showQr && (
          <Col style={{ textAlign: 'center' }}>
            <h3>Scan QR to setup MFA</h3>
            {qrCodeUri && <img src={qrCodeUri} alt="MFA QR Code" />}
            <p>Once registered in your app, enter the 6-digit code:</p>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              shouldAutoFocus
              renderInput={(props) => (
                <input
                  {...props}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otp.length === 6) {
                      handleVerifyMfa();
                    }
                  }}
                />
              )}
              inputStyle={{
                width: '2rem',
                height: '2rem',
                margin: '0 0.2rem',
                fontSize: '1.5rem',
                borderRadius: 4,
                border: '1px solid #ccc',
              }}
              containerStyle={{ justifyContent: 'center' }}
            />
            <div style={{ marginTop: 20 }}>
              <Button type="primary" onClick={handleVerifyMfa} disabled={otp.length !== 6}>
                Verify & Continue
              </Button>
            </div>
          </Col>
        )}

        {mfaStep && !showQr && (
          <div style={{ textAlign: 'center' }}>
            <h3>Enter MFA Code</h3>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              shouldAutoFocus
              renderInput={(props) => (
                <input
                  {...props}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otp.length === 6) {
                      handleVerifyMfa();
                    }
                  }}
                />
              )}
              inputStyle={{
                width: '2rem',
                height: '2rem',
                margin: '0 0.2rem',
                fontSize: '1.5rem',
                borderRadius: 4,
                border: '1px solid #ccc',
              }}
            />
            <div style={{ marginTop: 20 }}>
              <Button type="primary" onClick={handleVerifyMfa} disabled={otp.length !== 6}>
                Verify
              </Button>
              <Button
                style={{ marginLeft: 10 }}
                onClick={() => {
                  setMfaStep(false);
                  setOtp('');
                }}
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="main-login-page">
      <div className="login-wrapper">
        <img className="login-logo" src={logo} alt="Logo Sagem" />
        <Button
          onClick={() => {
            window.location.href = `${getBackendOrigin()}/saml2/authenticate/sota`;
          }}
          style={{ marginTop: '20px' }}
          className={styles.button}
          type="primary"
        >
          Log in with SAML
        </Button>
      </div>
    </div>
  );
}

export default Login;
